import { Component, ElementRef, OnInit, ViewChild, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TagModule } from 'primeng/tag';
import { ChartModule } from 'primeng/chart';
import { SelectModule } from 'primeng/select';
import { TooltipModule } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { TranslocoPipe } from '@jsverse/transloco';
import { SessionStorageService } from '../services/session-storage.service';
import { CustomerService } from '../customer/customer.service';
import { Role } from '../customer/customer-role.model';
import { DashboardWorkbenchService } from './dashboard-workbench.service';
import {
    DashboardCardSize,
    DashboardVisualization,
    WorkbenchDashboard,
    WorkbenchItem,
    WorkbenchModuleAccess,
    WorkbenchPoint,
    WorkbenchPreference,
    WorkbenchRow,
    WorkbenchWidgetOption,
    WorkbenchWidget
} from './dashboard-workbench.model';
import { PageUsageTrackerService } from './page-usage-tracker.service';
import { AppNavigationService } from '../shared/page-layout/app-navigation.service';
import { AppMenuBuilderService } from '../shared/page-layout/app-menu-builder.service';
import { TenantScopeService } from '../company/tenant-scope.service';
import { LocaleService } from '../i18n/locale.service';
import { SpringboardQuickAction, SpringboardTab, SpringboardTile } from './dashboard-springboard.model';
import { buildSpringboardTabs } from './dashboard-springboard.utils';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.component.html',
    styleUrls: ['./dashboard.component.css'],
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        DragDropModule,
        ButtonModule,
        DatePickerModule,
        InputTextModule,
        ProgressSpinnerModule,
        TagModule,
        ChartModule,
        SelectModule,
        TooltipModule,
        TranslocoPipe
    ]
})
export class DashboardComponent implements OnInit {
    dashboard?: WorkbenchDashboard;
    selectedDateRange: Date[] | null = null;
    selectedModule = 'ALL';
    selectedSeverity = 'ALL';
    searchTerm = '';
    density = 'comfortable';
    hiddenWidgetKeys = new Set<string>();
    widgetSizes: Record<string, DashboardCardSize> = {};
    widgetOptions: Record<string, WorkbenchWidgetOption> = {};
    loading = false;
    savingPreference = false;
    loadError = '';
    settingsOpen = false;
    preferenceMode: 'USER_OVERRIDE' | 'ROLE_DEFAULT' = 'USER_OVERRIDE';
    availableRoles: Role[] = [];
    selectedRoleName = '';
    loadingRolePreference = false;

    // Springboard state
    springboardTabs: SpringboardTab[] = [];
    activeTabId = 'main';
    activeSubGroup = 'All';

    // Things to Finish state
    showMoreThingsToFinish = false;
    @ViewChild('thingsTrack') thingsTrack?: ElementRef<HTMLDivElement>;

    private settingsSnapshot?: {
        density: string;
        hiddenWidgetKeys: string[];
        widgetSizes: Record<string, DashboardCardSize>;
        widgetOptions: Record<string, WorkbenchWidgetOption>;
        orderedWidgetKeys: string[];
    };
    private readonly chartDataCache = new Map<string, {
        payload: WorkbenchWidget['payload'];
        visualization: DashboardVisualization;
        value: any;
    }>();
    private readonly chartOptionsCache = new Map<string, {
        chartType: 'line' | 'bar' | 'doughnut';
        seriesCount: number;
        value: any;
    }>();

    readonly severityOptions = ['ALL', 'danger', 'warn', 'info', 'success'];
    readonly cardSizeOptions: Array<{ label: string; value: DashboardCardSize }> = [
        { label: 'Small', value: 'small' },
        { label: 'Medium', value: 'medium' },
        { label: 'Wide', value: 'wide' },
        { label: 'Full width', value: 'full' }
    ];
    readonly chartPalette = ['#3159d9', '#0b8f83', '#d48b18', '#c05670', '#7157d9', '#0789a5', '#64748b'];

    private dashboardWorkbenchService = inject(DashboardWorkbenchService);
    private sessionStorageService = inject(SessionStorageService);
    private customerService = inject(CustomerService);
    private messageService = inject(MessageService);
    private pageUsageTrackerService = inject(PageUsageTrackerService);
    private appNavigationService = inject(AppNavigationService);
    private appMenuBuilderService = inject(AppMenuBuilderService);
    private tenantScopeService = inject(TenantScopeService);
    private localeService = inject(LocaleService);
    readonly navigationDirection = this.localeService.direction;

    constructor() {
        // Reactively rebuild springboard tabs when navigation menu items or usage changes
        effect(() => {
            this.appNavigationService.menuItems();
            this.pageUsageTrackerService.usageSignal();
            this.localeService.locale();
            this.refreshSpringboard();
        });

        // Reactively listen for Home Reset trigger from top app bar
        effect(() => {
            const resetCount = this.appNavigationService.homeResetTrigger();
            if (resetCount > 0) {
                this.activeTabId = 'main';
                this.activeSubGroup = 'All';
                this.showMoreThingsToFinish = false;
                if (typeof window !== 'undefined') {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }
            }
        });
    }

    ngOnInit(): void {
        this.selectedDateRange = this.defaultDateRange();
        this.refreshSpringboard();
        this.loadDashboard();
    }

    refreshSpringboard(): void {
        let menuItems = this.appNavigationService.menuItems();
        if (!menuItems.length) {
            const { toolbarMenuItems } = this.appMenuBuilderService.buildMenus(() => {});
            menuItems = toolbarMenuItems;
        }

        const frequent = this.pageUsageTrackerService.getTopVisitedRoutes(15);
        const mainLabel = this.localeService.translate('dashboard.mainTab') || 'Main';
        this.springboardTabs = buildSpringboardTabs(menuItems, frequent, mainLabel);

        if (!this.springboardTabs.some(t => t.id === this.activeTabId)) {
            this.activeTabId = 'main';
        }
    }

    get activeTab(): SpringboardTab | undefined {
        return this.springboardTabs.find(t => t.id === this.activeTabId) || this.springboardTabs[0];
    }

    get activeTiles(): SpringboardTile[] {
        const tab = this.activeTab;
        if (!tab) {
            return [];
        }
        if (!this.activeSubGroup || this.activeSubGroup === 'All') {
            return tab.tiles;
        }
        return tab.tiles.filter(tile => tile.group === this.activeSubGroup);
    }

    selectSpringboardTab(tabId: string): void {
        this.activeTabId = tabId;
        this.activeSubGroup = 'All';
    }

    selectSubGroup(group: string): void {
        this.activeSubGroup = group;
    }

    onTileClick(tile: SpringboardTile): void {
        this.pageUsageTrackerService.recordVisit(tile.route);
    }

    onQuickActionClick(action: SpringboardQuickAction): void {
        this.pageUsageTrackerService.recordVisit(action.route);
    }

    scrollThings(direction: 'left' | 'right'): void {
        if (!this.thingsTrack?.nativeElement) {
            return;
        }
        const delta = direction === 'left' ? -360 : 360;
        this.thingsTrack.nativeElement.scrollBy({ left: delta, behavior: 'smooth' });
    }

    toggleThingsToFinishView(): void {
        this.showMoreThingsToFinish = !this.showMoreThingsToFinish;
    }

    get thingsToFinishItems(): WorkbenchItem[] {
        return this.dashboard?.items ?? [];
    }

    get severitySelectOptions(): Array<{ label: string; value: string }> {
        return this.severityOptions.map(severity => ({
            label: this.severityLabel(severity),
            value: severity
        }));
    }

    get roleSelectOptions(): Array<{ label: string; value: string }> {
        return this.availableRoles.map(role => ({
            label: role.roleName,
            value: role.roleName
        }));
    }

    loadDashboard(): void {
        const companyId = this.sessionStorageService.getCompany()?.id;
        const customerId = this.sessionStorageService.getCustomer()?.id;

        if (!companyId || !customerId) {
            this.loadError = 'Your company and user profile were not loaded. Please sign in again.';
            return;
        }

        const [fromDate, toDate] = this.resolvedDateRange();
        this.loading = true;
        this.loadError = '';

        this.dashboardWorkbenchService.getDashboard(companyId, customerId, fromDate, toDate).subscribe({
            next: dashboard => {
                this.dashboard = dashboard;
                this.applyPreference(dashboard.preference);
                if (!this.moduleKeys.includes(this.selectedModule)) {
                    this.selectedModule = 'ALL';
                }
                if (this.isAdmin && this.availableRoles.length === 0) {
                    this.loadAvailableRoles();
                }
                this.loading = false;
            },
            error: error => {
                this.loading = false;
                this.loadError = error?.error?.message || error?.message || 'Unable to load the dashboard workbench.';
            }
        });
    }

    onDateRangeChange(): void {
        this.loadDashboard();
    }

    selectModule(moduleKey: string): void {
        this.selectedModule = moduleKey;
    }

    toggleSettings(): void {
        if (this.settingsOpen) {
            this.restoreSettingsSnapshot();
            this.settingsOpen = false;
            return;
        }
        this.settingsSnapshot = {
            density: this.density,
            hiddenWidgetKeys: Array.from(this.hiddenWidgetKeys),
            widgetSizes: { ...this.widgetSizes },
            widgetOptions: structuredClone(this.widgetOptions),
            orderedWidgetKeys: this.dashboard?.widgets.map(widget => widget.key) ?? []
        };
        this.settingsOpen = true;
    }

    toggleWidget(widgetKey: string, checked: boolean): void {
        const next = new Set(this.hiddenWidgetKeys);
        if (checked) {
            next.delete(widgetKey);
        } else {
            next.add(widgetKey);
        }
        this.hiddenWidgetKeys = next;
    }

    removeWidget(widgetKey: string, event?: Event): void {
        event?.preventDefault();
        event?.stopPropagation();
        this.toggleWidget(widgetKey, false);
    }

    dropWidget(event: CdkDragDrop<WorkbenchWidget[]>): void {
        if (!this.dashboard || event.previousIndex === event.currentIndex) {
            return;
        }
        const visible = this.richWidgets;
        moveItemInArray(visible, event.previousIndex, event.currentIndex);
        const visibleKeys = new Set(visible.map(widget => widget.key));
        this.dashboard.widgets = [
            ...this.kpiWidgets,
            ...visible,
            ...this.dashboard.widgets.filter(widget => !visibleKeys.has(widget.key))
                .filter(widget => !this.kpiWidgets.some(kpi => kpi.key === widget.key))
        ];
        this.dashboard.widgets.forEach((widget, index) => widget.order = index);
    }

    setWidgetSize(widgetKey: string, size: DashboardCardSize): void {
        this.widgetSizes = { ...this.widgetSizes, [widgetKey]: size };
    }

    setVisualization(widget: WorkbenchWidget, visualization: DashboardVisualization): void {
        widget.visualization = visualization;
        this.widgetOptions = {
            ...this.widgetOptions,
            [widget.key]: { ...(this.widgetOptions[widget.key] ?? {}), visualization }
        };
    }

    setTopN(widgetKey: string, topN: number): void {
        this.widgetOptions = {
            ...this.widgetOptions,
            [widgetKey]: { ...(this.widgetOptions[widgetKey] ?? {}), topN }
        };
    }

    resetLayout(): void {
        if (!this.dashboard) {
            return;
        }
        this.hiddenWidgetKeys = new Set<string>();
        this.widgetSizes = {};
        this.widgetOptions = {};
        this.dashboard.widgets.sort((left, right) => this.defaultWidgetRank(left) - this.defaultWidgetRank(right));
        this.dashboard.widgets.forEach((widget, index) => widget.order = index);
        this.density = 'comfortable';
    }

    resetToRoleDefault(): void {
        const companyId = this.sessionStorageService.getCompany()?.id;
        const roleName = this.dashboard?.preference?.roleName || this.dashboard?.roleNames?.[0];
        if (!companyId || !roleName) {
            this.resetLayout();
            return;
        }
        this.loadingRolePreference = true;
        this.dashboardWorkbenchService.getPreference(companyId, 'ROLE_DEFAULT', roleName).subscribe({
            next: preference => {
                this.loadingRolePreference = false;
                this.applyPreference(preference);
                this.messageService.add({
                    severity: 'info',
                    summary: 'Role default restored',
                    detail: `Using the ${roleName} dashboard preset. Save to keep it as your personal layout.`
                });
            },
            error: () => {
                this.loadingRolePreference = false;
                this.resetLayout();
            }
        });
    }

    setPreferenceMode(mode: 'USER_OVERRIDE' | 'ROLE_DEFAULT'): void {
        if (mode === 'ROLE_DEFAULT' && !this.isAdmin) {
            return;
        }
        this.preferenceMode = mode;
        if (mode === 'USER_OVERRIDE') {
            this.applyPreference(this.dashboard?.preference);
            return;
        }
        if (!this.selectedRoleName) {
            this.selectedRoleName = this.availableRoles[0]?.roleName || this.dashboard?.roleNames?.[0] || '';
        }
        this.loadSelectedRolePreference();
    }

    loadSelectedRolePreference(): void {
        const companyId = this.sessionStorageService.getCompany()?.id;
        if (!companyId || !this.selectedRoleName || this.preferenceMode !== 'ROLE_DEFAULT') {
            return;
        }
        this.loadingRolePreference = true;
        this.dashboardWorkbenchService.getPreference(companyId, 'ROLE_DEFAULT', this.selectedRoleName).subscribe({
            next: preference => {
                this.loadingRolePreference = false;
                this.applyPreference(preference);
            },
            error: error => {
                this.loadingRolePreference = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Preset unavailable',
                    detail: error?.error?.message || 'Unable to load the selected role preset.'
                });
            }
        });
    }

    savePreferences(): void {
        const companyId = this.sessionStorageService.getCompany()?.id;
        const customerId = this.sessionStorageService.getCustomer()?.id;
        if (!companyId || (!customerId && this.preferenceMode === 'USER_OVERRIDE')) {
            return;
        }

        const config = {
            ...(this.dashboard?.preference?.config ?? {}),
            density: this.density,
            hiddenWidgets: Array.from(this.hiddenWidgetKeys),
            orderedWidgets: this.dashboard?.widgets?.map(widget => widget.key) ?? [],
            widgetSizes: this.widgetSizes,
            widgetOptions: this.widgetOptions,
            pageUsage: this.pageUsageTrackerService.toPreferencePayload()
        };
        const preference: WorkbenchPreference = {
            companyId,
            customerId: this.preferenceMode === 'USER_OVERRIDE' ? customerId : undefined,
            roleName: this.preferenceMode === 'ROLE_DEFAULT' ? this.selectedRoleName : undefined,
            scopeType: this.preferenceMode,
            config
        };

        this.savingPreference = true;
        this.dashboardWorkbenchService.savePreference(preference).subscribe({
            next: savedPreference => {
                this.savingPreference = false;
                if (this.dashboard && this.preferenceMode === 'USER_OVERRIDE') {
                    this.dashboard.preference = savedPreference;
                }
                this.applyPreference(savedPreference);
                this.settingsOpen = false;
                this.settingsSnapshot = undefined;
                this.messageService.add({
                    severity: 'success',
                    summary: this.preferenceMode === 'ROLE_DEFAULT' ? 'Role preset published' : 'Dashboard saved',
                    detail: this.preferenceMode === 'ROLE_DEFAULT'
                        ? `${this.selectedRoleName} now has a new default dashboard.`
                        : 'Your workbench layout has been updated.'
                });
            },
            error: error => {
                this.savingPreference = false;
                this.messageService.add({ severity: 'error', summary: 'Save failed', detail: error?.error?.message || error?.message || 'Unable to save dashboard preferences.' });
            }
        });
    }

    get modules(): WorkbenchModuleAccess[] {
        return this.dashboard?.availableModules ?? [];
    }

    get enabledModules(): WorkbenchModuleAccess[] {
        return this.modules.filter(module => module.enabled);
    }

    get moduleKeys(): string[] {
        return ['ALL', ...this.enabledModules.map(module => module.key)];
    }

    get visibleWidgets(): WorkbenchWidget[] {
        return (this.dashboard?.widgets ?? [])
            .filter(widget => !this.hiddenWidgetKeys.has(widget.key))
            .sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    }

    get kpiWidgets(): WorkbenchWidget[] {
        return this.visibleWidgets.filter(widget => this.isKpiWidget(widget));
    }

    get richWidgets(): WorkbenchWidget[] {
        return this.visibleWidgets.filter(widget => !this.isKpiWidget(widget));
    }

    get allWidgets(): WorkbenchWidget[] {
        return this.dashboard?.widgets ?? [];
    }

    get isAdmin(): boolean {
        return (this.dashboard?.roleNames ?? []).some(role =>
            /admin|owner|super/i.test(role)
        );
    }

    get filteredItems(): WorkbenchItem[] {
        const term = this.searchTerm.trim().toLowerCase();
        return (this.dashboard?.items ?? []).filter(item => {
            const moduleMatch = this.selectedModule === 'ALL' || item.module === this.selectedModule;
            const severityMatch = this.selectedSeverity === 'ALL' || item.severity === this.selectedSeverity;
            const searchMatch = !term
                || item.title?.toLowerCase().includes(term)
                || item.subtitle?.toLowerCase().includes(term)
                || item.status?.toLowerCase().includes(term)
                || item.type?.toLowerCase().includes(term);
            return moduleMatch && severityMatch && searchMatch;
        });
    }

    get hasNoAccess(): boolean {
        return !this.loading && !this.loadError && this.enabledModules.length === 0;
    }

    widgetValue(widget: WorkbenchWidget): string {
        const metric = widget.payload?.metric;
        if (metric?.value !== undefined && metric?.value !== null) {
            return this.formatValue(metric.value, metric.format, metric.unit || widget.unit);
        }
        if (widget.amount !== undefined && widget.amount !== null) {
            const unit = widget.unit && widget.unit.length <= 3 ? `${widget.unit} ` : '';
            return `${unit}${Number(widget.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
        }
        return `${widget.count ?? 0}`;
    }

    comparisonText(widget: WorkbenchWidget): string {
        const comparison = widget.payload?.comparison;
        if (!comparison) {
            return 'Selected period';
        }
        if (comparison.status === 'NEW') {
            return 'New vs previous period';
        }
        if (comparison.deltaPercent === undefined || comparison.deltaPercent === null) {
            return comparison.label || 'vs previous period';
        }
        const arrow = comparison.direction === 'UP' ? '↑' : comparison.direction === 'DOWN' ? '↓' : '→';
        return `${arrow} ${Math.abs(comparison.deltaPercent).toLocaleString(undefined, { maximumFractionDigits: 1 })}% ${comparison.label || ''}`.trim();
    }

    isKpiWidget(widget: WorkbenchWidget): boolean {
        return ['KPI', 'SUMMARY', 'KPI_TREND'].includes(widget.type);
    }

    visualization(widget: WorkbenchWidget): DashboardVisualization {
        return this.widgetOptions[widget.key]?.visualization
            || widget.visualization
            || widget.capabilities?.defaultVisualization
            || 'SCORECARD';
    }

    visualizationLabel(value: DashboardVisualization): string {
        const labels: Record<string, string> = {
            SCORECARD: 'Scorecard',
            LINE: 'Line',
            AREA: 'Area',
            BAR: 'Bar',
            DOUGHNUT: 'Donut',
            PROGRESS_LIST: 'Progress list',
            FUNNEL: 'Funnel',
            LIST: 'List'
        };
        return labels[value] ?? value.replaceAll('_', ' ').toLowerCase();
    }

    isChartCard(widget: WorkbenchWidget): boolean {
        return ['LINE', 'AREA', 'BAR', 'DOUGHNUT'].includes(this.visualization(widget))
            && ((widget.payload?.series?.length ?? 0) > 0 || (widget.payload?.points?.length ?? 0) > 0);
    }

    chartType(widget: WorkbenchWidget): 'line' | 'bar' | 'doughnut' {
        const visualization = this.visualization(widget);
        if (visualization === 'DOUGHNUT') {
            return 'doughnut';
        }
        if (visualization === 'BAR') {
            return 'bar';
        }
        return 'line';
    }

    chartData(widget: WorkbenchWidget): any {
        const payload = widget.payload;
        const visualization = this.visualization(widget);
        const cached = this.chartDataCache.get(widget.key);
        if (cached && cached.payload === payload && cached.visualization === visualization) {
            return cached.value;
        }

        let value: any;
        if ((payload?.series?.length ?? 0) > 0) {
            value = {
                labels: payload?.labels ?? [],
                datasets: (payload?.series ?? []).map((series, index) => ({
                    label: series.label || series.key,
                    data: series.data ?? [],
                    borderColor: series.color || this.chartPalette[index % this.chartPalette.length],
                    backgroundColor: visualization === 'AREA'
                        ? this.withAlpha(series.color || this.chartPalette[index % this.chartPalette.length], 0.18)
                        : series.color || this.chartPalette[index % this.chartPalette.length],
                    fill: visualization === 'AREA' || !!series.fill,
                    tension: 0.32,
                    borderWidth: 2,
                    pointRadius: (payload?.labels?.length ?? 0) > 20 ? 0 : 2
                }))
            };
        } else {
            const points = payload?.points ?? [];
            value = {
                labels: points.map(point => point.label),
                datasets: [{
                    label: widget.title,
                    data: points.map(point => point.value ?? 0),
                    backgroundColor: points.map((_, index) => this.chartPalette[index % this.chartPalette.length]),
                    borderWidth: 0
                }]
            };
        }

        this.chartDataCache.set(widget.key, { payload, visualization, value });
        return value;
    }

    chartOptions(widget: WorkbenchWidget): any {
        const chartType = this.chartType(widget);
        const seriesCount = widget.payload?.series?.length ?? 0;
        const cached = this.chartOptionsCache.get(widget.key);
        if (cached && cached.chartType === chartType && cached.seriesCount === seriesCount) {
            return cached.value;
        }

        const doughnut = chartType === 'doughnut';
        const value = {
            responsive: true,
            maintainAspectRatio: false,
            resizeDelay: 150,
            animation: false,
            plugins: {
                legend: {
                    display: doughnut || seriesCount > 1,
                    position: doughnut ? 'right' : 'bottom',
                    labels: { usePointStyle: true, boxWidth: 8, color: '#65738a' }
                },
                tooltip: { intersect: false, mode: doughnut ? 'nearest' : 'index' }
            },
            scales: doughnut ? undefined : {
                x: { grid: { display: false }, ticks: { color: '#7b8799', maxRotation: 0, autoSkip: true } },
                y: { beginAtZero: true, grid: { color: 'rgba(148, 163, 184, .16)' }, ticks: { color: '#7b8799' } }
            }
        };
        this.chartOptionsCache.set(widget.key, { chartType, seriesCount, value });
        return value;
    }

    cardPoints(widget: WorkbenchWidget): WorkbenchPoint[] {
        return (widget.payload?.points ?? []).slice(0, this.topN(widget));
    }

    cardRows(widget: WorkbenchWidget): WorkbenchRow[] {
        return (widget.payload?.rows ?? []).slice(0, this.topN(widget));
    }

    topN(widget: WorkbenchWidget): number {
        return this.widgetOptions[widget.key]?.topN
            ?? widget.capabilities?.topNOptions?.[0]
            ?? 10;
    }

    progressWidth(value?: number, values?: Array<{ value?: number }>): number {
        const maximum = Math.max(1, ...(values ?? []).map(item => Number(item.value ?? 0)));
        return Math.max(3, Math.min(100, (Number(value ?? 0) / maximum) * 100));
    }

    formatPointValue(point: WorkbenchPoint): string {
        return this.formatValue(point.value ?? 0, point.unit === 'SAR' ? 'CURRENCY' : 'COUNT', point.unit);
    }

    allowedCardSizes(widget: WorkbenchWidget): Array<{ label: string; value: DashboardCardSize }> {
        const ranks: Record<DashboardCardSize, number> = { small: 0, medium: 1, wide: 2, full: 3 };
        const minimum = widget.capabilities?.minimumSize ?? 'small';
        return this.cardSizeOptions.filter(option => ranks[option.value] >= ranks[minimum]);
    }

    cardSize(widget: WorkbenchWidget): DashboardCardSize {
        return this.widgetSizes[widget.key] ?? this.defaultCardSize(widget);
    }

    cardIcon(widget: WorkbenchWidget): string {
        const keyIcons: Record<string, string> = {
            open_items: 'pi pi-inbox',
            overdue_items: 'pi pi-clock',
            due_today: 'pi pi-calendar-clock',
            high_priority: 'pi pi-bolt',
            productivity_tasks: 'pi pi-check-square',
            productivity_notifications: 'pi pi-bell',
            productivity_incidents: 'pi pi-exclamation-circle',
            shipments_open: 'pi pi-truck',
            shipments_exceptions: 'pi pi-exclamation-triangle',
            shipments_ready_to_invoice: 'pi pi-file-check',
            crm_open_pipeline: 'pi pi-chart-line',
            crm_overdue_followups: 'pi pi-phone',
            crm_at_risk: 'pi pi-shield',
            containers_open_cases: 'pi pi-box',
            containers_exceptions: 'pi pi-exclamation-triangle',
            containers_empty_return: 'pi pi-replay',
            expense_approvals: 'pi pi-receipt'
        };
        return keyIcons[widget.key] ?? this.moduleIcon(widget.module);
    }

    cardDescription(widget: WorkbenchWidget): string {
        if (widget.subtitle) {
            return widget.subtitle;
        }
        const descriptions: Record<string, string> = {
            open_items: 'Actionable work across your permitted modules',
            overdue_items: 'Items that need immediate attention',
            due_today: 'Work reaching its due date today',
            high_priority: 'Critical and high-priority work',
            shipments_open: 'Active logistics jobs in the selected period',
            shipments_exceptions: 'Shipments with active operational exceptions',
            shipments_ready_to_invoice: 'Completed shipment value awaiting billing',
            crm_open_pipeline: 'Current sales pipeline value',
            crm_overdue_followups: 'Customer follow-ups past their due date',
            crm_at_risk: 'Leads and opportunities requiring attention',
            containers_open_cases: 'Containers currently under active tracking',
            containers_exceptions: 'Container lifecycle exceptions',
            containers_empty_return: 'Empty containers awaiting return',
            expense_approvals: 'Expense requests waiting for approval'
        };
        return descriptions[widget.key] ?? `Live ${this.moduleLabel(widget.module).toLowerCase()} metric`;
    }

    cardAccent(widget: WorkbenchWidget): string {
        const accents: Record<string, string> = {
            WORKBENCH: 'violet',
            PRODUCTIVITY: 'amber',
            SHIPMENTS: 'blue',
            ACCOUNTS: 'teal',
            CRM: 'indigo',
            CONTAINER_TRACKING: 'cyan',
            EXPENSE: 'rose'
        };
        return accents[widget.module] ?? 'slate';
    }

    get greeting(): string {
        const hour = new Date().getHours();
        if (hour < 12) {
            return 'Good morning';
        }
        if (hour < 17) {
            return 'Good afternoon';
        }
        return 'Good evening';
    }

    get customerName(): string {
        return this.sessionStorageService.getCustomer()?.name || 'there';
    }

    itemRoute(item: WorkbenchItem): string {
        return item.actions?.[0]?.route || item.route || '/dashboard';
    }

    itemAgeText(item: WorkbenchItem): string {
        if (item.ageHours !== undefined && item.ageHours !== null) {
            if (item.ageHours < 24) {
                return `${Math.max(1, Math.round(item.ageHours))}h ago`;
            }
            if (item.ageHours < 168) {
                return `${Math.round(item.ageHours / 24)}d ago`;
            }
            return `${Math.round(item.ageHours / 168)}w ago`;
        }
        return '';
    }

    moduleLabel(moduleKey: string): string {
        if (moduleKey === 'ALL') {
            return 'All work';
        }
        return this.modules.find(module => module.key === moduleKey)?.label ?? moduleKey.replaceAll('_', ' ');
    }

    moduleIcon(moduleKey: string): string {
        const icons: Record<string, string> = {
            ALL: 'pi pi-list',
            PRODUCTIVITY: 'pi pi-bell',
            SHIPMENTS: 'pi pi-truck',
            ACCOUNTS: 'pi pi-wallet',
            CRM: 'pi pi-users',
            CONTAINER_TRACKING: 'pi pi-box',
            EXPENSE: 'pi pi-check-square',
            WORKBENCH: 'pi pi-briefcase'
        };
        return icons[moduleKey] ?? 'pi pi-circle';
    }

    severityLabel(severity: string): string {
        if (severity === 'ALL') {
            return 'All severities';
        }
        return severity.charAt(0).toUpperCase() + severity.slice(1);
    }

    private applyPreference(preference?: WorkbenchPreference): void {
        const config = preference?.config ?? {};
        this.density = typeof config['density'] === 'string' ? config['density'] : 'comfortable';
        this.hiddenWidgetKeys = new Set(Array.isArray(config['hiddenWidgets']) ? config['hiddenWidgets'] as string[] : []);
        const savedSizes = config['widgetSizes'];
        this.widgetSizes = savedSizes && typeof savedSizes === 'object' && !Array.isArray(savedSizes)
            ? savedSizes as Record<string, DashboardCardSize>
            : {};
        const savedOptions = config['widgetOptions'];
        this.widgetOptions = savedOptions && typeof savedOptions === 'object' && !Array.isArray(savedOptions)
            ? savedOptions as Record<string, WorkbenchWidgetOption>
            : {};
        this.dashboard?.widgets.forEach(widget => {
            const visualization = this.widgetOptions[widget.key]?.visualization;
            if (visualization) {
                widget.visualization = visualization;
            }
        });
        const order = Array.isArray(config['orderedWidgets']) ? config['orderedWidgets'] as string[] : [];
        if (this.dashboard && order.length) {
            this.dashboard.widgets.sort((left, right) => {
                const leftIndex = order.indexOf(left.key);
                const rightIndex = order.indexOf(right.key);
                return (leftIndex < 0 ? Number.MAX_SAFE_INTEGER : leftIndex)
                    - (rightIndex < 0 ? Number.MAX_SAFE_INTEGER : rightIndex);
            });
            this.dashboard.widgets.forEach((widget, index) => widget.order = index);
        }

        const pageUsage = config['pageUsage'];
        if (pageUsage && typeof pageUsage === 'object') {
            this.pageUsageTrackerService.syncFromPreference(pageUsage as Record<string, unknown>);
        }
    }

    private defaultCardSize(widget: WorkbenchWidget): DashboardCardSize {
        if (widget.capabilities?.defaultSize) {
            return widget.capabilities.defaultSize;
        }
        if (widget.module === 'WORKBENCH') {
            return 'small';
        }
        if (widget.amount !== undefined && widget.amount !== null) {
            return 'medium';
        }
        return 'small';
    }

    private defaultWidgetRank(widget: WorkbenchWidget): number {
        const featuredOrder = [
            'open_items',
            'overdue_items',
            'due_today',
            'high_priority',
            'shipments_open',
            'shipments_exceptions',
            'shipments_ready_to_invoice',
            'crm_open_pipeline',
            'crm_overdue_followups',
            'crm_at_risk',
            'containers_open_cases',
            'containers_exceptions',
            'containers_empty_return',
            'productivity_tasks',
            'productivity_notifications',
            'productivity_incidents',
            'expense_approvals'
        ];
        const featuredRank = featuredOrder.indexOf(widget.key);
        if (featuredRank >= 0) {
            return featuredRank;
        }
        const moduleRanks: Record<string, number> = {
            WORKBENCH: 100,
            SHIPMENTS: 200,
            ACCOUNTS: 300,
            CRM: 400,
            CONTAINER_TRACKING: 500,
            PRODUCTIVITY: 600,
            EXPENSE: 700
        };
        return (moduleRanks[widget.module] ?? 900) + (widget.order ?? 0);
    }

    private restoreSettingsSnapshot(): void {
        if (!this.settingsSnapshot) {
            return;
        }
        this.density = this.settingsSnapshot.density;
        this.hiddenWidgetKeys = new Set(this.settingsSnapshot.hiddenWidgetKeys);
        this.widgetSizes = { ...this.settingsSnapshot.widgetSizes };
        this.widgetOptions = structuredClone(this.settingsSnapshot.widgetOptions);
        if (this.dashboard) {
            const order = this.settingsSnapshot.orderedWidgetKeys;
            this.dashboard.widgets.sort((left, right) => {
                const leftIndex = order.indexOf(left.key);
                const rightIndex = order.indexOf(right.key);
                return (leftIndex < 0 ? Number.MAX_SAFE_INTEGER : leftIndex)
                    - (rightIndex < 0 ? Number.MAX_SAFE_INTEGER : rightIndex);
            });
            this.dashboard.widgets.forEach((widget, index) => widget.order = index);
        }
        this.settingsSnapshot = undefined;
    }

    private loadAvailableRoles(): void {
        this.customerService.getAllRoles('COMPANY').subscribe({
            next: roles => {
                this.availableRoles = (roles ?? [])
                    .filter(role => !!role.roleName)
                    .sort((left, right) => left.roleName.localeCompare(right.roleName));
                if (!this.selectedRoleName) {
                    this.selectedRoleName = this.availableRoles.find(role =>
                        this.dashboard?.roleNames?.includes(role.roleName)
                    )?.roleName || this.availableRoles[0]?.roleName || '';
                }
            },
            error: () => this.availableRoles = []
        });
    }

    private resolvedDateRange(): [Date | undefined, Date | undefined] {
        if (!this.selectedDateRange?.length) {
            return [undefined, undefined];
        }
        const fromDate = this.selectedDateRange[0] ?? undefined;
        const toDate = this.selectedDateRange[1] ?? this.selectedDateRange[0] ?? undefined;
        return [fromDate, toDate];
    }

    private defaultDateRange(): Date[] {
        const toDate = new Date();
        const fromDate = new Date();
        fromDate.setDate(toDate.getDate() - 29);
        return [fromDate, toDate];
    }

    private formatValue(value: number, format?: string, unit?: string): string {
        if (format === 'PERCENT' || unit === '%') {
            return `${Number(value).toLocaleString(undefined, { maximumFractionDigits: 1 })}%`;
        }
        if (format === 'CURRENCY' || unit === 'SAR') {
            return `SAR ${Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
        }
        return Number(value).toLocaleString(undefined, { maximumFractionDigits: 2 });
    }

    private withAlpha(color: string, alpha: number): string {
        const normalized = color.replace('#', '');
        if (!/^[0-9a-f]{6}$/i.test(normalized)) {
            return color;
        }
        const red = Number.parseInt(normalized.slice(0, 2), 16);
        const green = Number.parseInt(normalized.slice(2, 4), 16);
        const blue = Number.parseInt(normalized.slice(4, 6), 16);
        return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
    }
}
